import { getToken, removeToken } from './auth';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

class ApiError extends Error {
  constructor(message, status, detail) {
    super(String(message || 'Bir hata oluştu'));
    this.status = status;
    // `detail` is the parsed body's `detail` field — useful for 402 INSUFFICIENT_CREDITS
    // payloads which are { code, message_tr, current_balance, required_credits, feature_key }.
    this.detail = detail;
    this.name = 'ApiError';
  }
}

export { ApiError };

export async function fetchWithAuth(endpoint, options = {}) {
  const { skipAuth = false, ...fetchOptions } = options;
  
  const headers = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers || {}),
  };
  
  if (!skipAuth) {
    // Primary token (doctor / super-admin / employee) lives under 'neobreed_token'.
    // The B2C Mom app uses its own isolated token under 'mom_auth_token'. When
    // shared API helpers (e.g. creditsApi) are invoked from mom-side pages we
    // fall back to the mom token so backend endpoints that accept BOTH roles
    // (like /api/credits/me — guarded by `get_current_user` only) keep working.
    const token =
      getToken() || (typeof window !== 'undefined' ? localStorage.getItem('mom_auth_token') : null);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });
  
  if (!response.ok) {
    // Read body as text first, then parse — `response.json()` can reject if
    // the body stream was already consumed by emergent-main.js's fetch
    // interceptor. The text-based fallback always works on a fresh stream
    // and on iOS Safari where some streaming reads are flaky.
    const raw = await response.text().catch(() => '');
    let errorData = {};
    try { errorData = raw ? JSON.parse(raw) : {}; } catch { /* non-JSON body */ }

    if (response.status === 401) {
      removeToken();
      // Signal auth loss — AuthContext listens and clears user state
      // ProtectedRoute then handles the redirect via React Router (no hard reload)
      window.dispatchEvent(new Event('auth:unauthorized'));
      throw new ApiError(
        errorData.detail || raw || 'Oturum süresi doldu',
        401,
      );
    }

    // For HTTP 402 (INSUFFICIENT_CREDITS) the backend returns a structured detail object.
    // Pass it through so the caller can render a credits modal.
    const detail = errorData?.detail;
    const message = (typeof detail === 'string')
      ? detail
      : (detail?.message_tr || detail?.message || raw || 'Bir hata oluştu');
    throw new ApiError(message, response.status, detail);
  }

  return response.json();
}

// Auth API
export const authApi = {
  login: (credentials) => 
    fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
      skipAuth: true,
    }),
  register: (data) =>
    fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    }),
  registerUser: (data) =>
    fetchWithAuth('/auth/register-user', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  me: () => fetchWithAuth('/auth/me'),
  completeOnboarding: () => fetchWithAuth('/auth/onboarding-complete', { method: 'POST' }),
  logout: () => 
    fetchWithAuth('/auth/logout', {
      method: 'POST',
    }),
  updateProfile: (data) =>
    fetchWithAuth('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  changePassword: (data) =>
    fetchWithAuth('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  changeEmail: (data) =>
    fetchWithAuth('/auth/change-email', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  myModules: () => fetchWithAuth('/auth/my-modules'),
  requestPasswordReset: (email) =>
    fetchWithAuth('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    }),
  resetPassword: (data) =>
    fetchWithAuth('/auth/password-reset/reset', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    }),
};

// Patients API
export const patientsApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithAuth(`/patients?${query}`);
  },
  get: (id) => fetchWithAuth(`/patients/${id}`),
  create: (data) =>
    fetchWithAuth('/patients', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    fetchWithAuth(`/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) =>
    fetchWithAuth(`/patients/${id}`, {
      method: 'DELETE',
    }),
};

// Scans API
export const scansApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithAuth(`/scans?${query}`);
  },
  get: (id) => fetchWithAuth(`/scans/${id}`),
  create: (data) =>
    fetchWithAuth('/scans', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    fetchWithAuth(`/scans/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id) =>
    fetchWithAuth(`/scans/${id}`, {
      method: 'DELETE',
    }),
  getForPatient: (patientId) =>
    fetchWithAuth(`/scans/patient/${patientId}`),
  getByPatient: (patientId) =>
    fetchWithAuth(`/scans/patient/${patientId}`),
  getResult: (scanId) =>
    fetchWithAuth(`/scans/${scanId}/result`),
  uploadImage: async (scanId, imageData, imageType = 'profile') => {
    return fetchWithAuth(`/scans/${scanId}/upload-image`, {
      method: 'POST',
      body: JSON.stringify({ image_data: imageData, image_type: imageType }),
    });
  },
};

// Users API (Super Admin)
export const usersApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();
    return fetchWithAuth(qs ? `/users?${qs}` : '/users');
  },
  get: (id) => fetchWithAuth(`/users/${id}`),
  create: (data) =>
    fetchWithAuth('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    fetchWithAuth(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) =>
    fetchWithAuth(`/users/${id}`, {
      method: 'DELETE',
    }),
};

// Clinics API
export const clinicsApi = {
  list: () => fetchWithAuth('/clinics'),
  get: (id) => fetchWithAuth(`/clinics/${id}`),
  create: (data) =>
    fetchWithAuth('/clinics', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    fetchWithAuth(`/clinics/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) =>
    fetchWithAuth(`/clinics/${id}`, {
      method: 'DELETE',
    }),
};

// Packages API
export const packagesApi = {
  list: () => fetchWithAuth('/packages'),
  listPublic: () => fetchWithAuth('/packages/public', { skipAuth: true }),
  get: (id) => fetchWithAuth(`/packages/${id}`),
  create: (data) =>
    fetchWithAuth('/packages', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    fetchWithAuth(`/packages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id) =>
    fetchWithAuth(`/packages/${id}`, {
      method: 'DELETE',
    }),
};

export const creditsApi = {
  // Credit System v2 — user-facing endpoints
  // Single source of truth: backend. Frontend never decides credit logic.
  getMyCredits: () => fetchWithAuth('/credits/me'),
  getMyHistory: (limit = 50) => fetchWithAuth(`/credits/history?limit=${limit}`),
  getPackages: () => fetchWithAuth('/credits/packages'),
  redeem: (code) =>
    fetchWithAuth('/credits/redeem', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  buyRequest: (data) =>
    fetchWithAuth('/credits/buy-request', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Credit System v2 — Admin endpoints (Super Admin + Credit Admin).
// Frontend role checks are advisory; backend always enforces.
// Credit-Admin Doctors (also accessible to super_admin)
export const creditAdminDoctorsApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchWithAuth(`/credit-admin/doctors${q ? `?${q}` : ''}`);
  },
  create: (data) =>
    fetchWithAuth('/credit-admin/doctors', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  resetPassword: (doctorId) =>
    fetchWithAuth(`/credit-admin/doctors/${doctorId}/reset-password`, {
      method: 'POST',
    }),
};

export const adminCreditsApi = {
  // ── Super Admin only ────────────────────────────────────────────────────
  overview: () => fetchWithAuth('/admin/credits/overview'),
  listUsers: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchWithAuth(`/admin/credits/users${q ? `?${q}` : ''}`);
  },
  getUser: (userId) => fetchWithAuth(`/admin/credits/user/${userId}`),
  getUserHistory: (userId, limit = 100) =>
    fetchWithAuth(`/admin/credits/user/${userId}/history?limit=${limit}`),
  grant: (data) =>
    fetchWithAuth('/admin/credits/grant', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  setUnlimited: (data) =>
    fetchWithAuth('/admin/credits/set-unlimited', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  manualRefund: (data) =>
    fetchWithAuth('/admin/credits/manual-refund', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  listPricing: () => fetchWithAuth('/admin/credits/pricing'),
  updatePricing: (userType, pricePerCredit) =>
    fetchWithAuth(`/admin/credits/pricing/${userType}`, {
      method: 'PUT',
      body: JSON.stringify({ price_per_credit: pricePerCredit }),
    }),
  listFeatureCosts: () => fetchWithAuth('/admin/credits/feature-costs'),
  updateFeatureCost: (featureKey, body) =>
    fetchWithAuth(`/admin/credits/feature-costs/${featureKey}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  listAllPackages: () => fetchWithAuth('/admin/credits/packages'),
  // ── Buy-Request manual approval (PHASE 3.A) ────────────────────────────
  listBuyRequests: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchWithAuth(`/admin/credits/buy-requests${q ? `?${q}` : ''}`);
  },
  fulfillBuyRequest: (id, body = {}) =>
    fetchWithAuth(`/admin/credits/buy-requests/${id}/fulfill`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rejectBuyRequest: (id, reason) =>
    fetchWithAuth(`/admin/credits/buy-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ── Credit Admin (and Super Admin too) ─────────────────────────────────
  listDoctors: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchWithAuth(`/admin/credits/doctors${q ? `?${q}` : ''}`);
  },
  getDoctor: (userId) => fetchWithAuth(`/admin/credits/doctor/${userId}`),
  grantSmall: (userId, amount, note) =>
    fetchWithAuth(`/admin/credits/doctor/${userId}/grant-small`, {
      method: 'POST',
      body: JSON.stringify({ amount, note }),
    }),
};

// Credit System v2 — Promo admin endpoints (Super Admin only)
export const adminPromoApi = {
  createCampaign: (data) =>
    fetchWithAuth('/admin/promo/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  listCampaigns: (onlyActive = false) =>
    fetchWithAuth(`/admin/promo/campaigns${onlyActive ? '?only_active=true' : ''}`),
  getCampaign: (id) => fetchWithAuth(`/admin/promo/campaigns/${id}`),
  setCampaignActive: (id, isActive) =>
    fetchWithAuth(`/admin/promo/campaigns/${id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    }),
  generateCodes: (campaignId, body) =>
    fetchWithAuth(`/admin/promo/campaigns/${campaignId}/codes/generate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listCodes: (campaignId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchWithAuth(
      `/admin/promo/campaigns/${campaignId}/codes${q ? `?${q}` : ''}`,
    );
  },
  disableCode: (codeId) =>
    fetchWithAuth(`/admin/promo/codes/${codeId}/disable`, { method: 'PATCH' }),
  // CSV/PDF download — needs browser download, returns absolute URL (caller
  // appends Authorization header via a tiny fetch+blob helper since the
  // browser cannot send headers on a plain <a download>).
  exportCsvUrl: (campaignId) =>
    `${API_URL}/admin/promo/campaigns/${campaignId}/codes/export.csv`,
  exportPdfUrl: (campaignId) =>
    `${API_URL}/admin/promo/campaigns/${campaignId}/codes/export.pdf`,
};

// Helper: download a protected URL via fetch+blob (since browsers cannot
// attach Authorization headers to a plain <a href>).
export const downloadAuthenticated = async (url, filename) => {
  const token =
    getToken() || (typeof window !== 'undefined' ? localStorage.getItem('mom_auth_token') : null);
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(`Export failed (HTTP ${res.status})`, res.status);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
};

// Generation API
export const generationApi = {
  startUltrasound4K: (data) =>
    fetchWithAuth('/generation/ultrasound-4k-scientific/start', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getUltrasound4KStatus: (jobId) =>
    fetchWithAuth(`/generation/ultrasound-4k-scientific/status/${jobId}`),
  validateImage: (data) =>
    fetchWithAuth('/generation/validate-image', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  extractBiometrics: (imageData) =>
    fetchWithAuth('/generation/extract-biometrics', {
      method: 'POST',
      body: JSON.stringify(typeof imageData === 'string' ? { image_base64: imageData } : imageData),
    }),
  analyzeBiometrics: (data) =>
    fetchWithAuth('/generation/analyze-biometrics', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  generateParametricFace: (data) =>
    fetchWithAuth('/generation/parametric-face', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  generate4DSimulation: (data) =>
    fetchWithAuth('/generation/4d-simulation', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  generateAIFace: (data) =>
    fetchWithAuth('/generation/ai-face', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  generateBlenderMesh: (data) =>
    fetchWithAuth('/generation/blender-mesh', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  healthCheck: () =>
    fetchWithAuth('/generation/health-check', { skipAuth: true }),
  // Aliases for frontend compatibility
  startScientificUltrasound4K: (data) =>
    fetchWithAuth('/generation/ultrasound-4k-scientific/start', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getScientificUltrasound4KStatus: (jobId) =>
    fetchWithAuth(`/generation/ultrasound-4k-scientific/status/${jobId}`),
  validateUltrasoundImage: (imageData) =>
    fetchWithAuth('/generation/validate-ultrasound-image', {
      method: 'POST',
      body: JSON.stringify(typeof imageData === 'string' ? { image_base64: imageData } : imageData),
    }),
  generateAiBabyFace: (data) =>
    fetchWithAuth('/generation/ai-baby-face', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  parametricFace: (measurements, gender, format) =>
    fetchWithAuth('/generation/parametric-face', {
      method: 'POST',
      body: JSON.stringify({ measurements, gender, format }),
    }),
  // Missing methods
  validateImagePair: (sideImage, frontImage) =>
    fetchWithAuth('/generation/validate-image-pair', {
      method: 'POST',
      body: JSON.stringify({ side_image_base64: sideImage, front_image_base64: frontImage }),
    }),
  generate: (scanId, type) =>
    fetchWithAuth(`/generation/scan/${scanId}/generate`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    }),
};

// Activity Logs API
export const activityLogsApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithAuth(`/activity?${query}`);
  },
};

// B2C App Users API
export const appUsersApi = {
  register: (data) =>
    fetchWithAuth('/app-auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    }),
  login: (credentials) =>
    fetchWithAuth('/app-auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
      skipAuth: true,
    }),
  me: () => fetchWithAuth('/app-auth/me'),
  verifyEmail: (data) =>
    fetchWithAuth('/app-auth/verify-email', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    }),
  resendVerification: (email) =>
    fetchWithAuth('/app-auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    }),
};

// Women's Health API
export const womensHealthApi = {
  createEncounter: (data) =>
    fetchWithAuth('/womens-health/encounters', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  listEncounters: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchWithAuth(`/womens-health/encounters?${query}`);
  },
  getEncounter: (id) =>
    fetchWithAuth(`/womens-health/encounters/${id}`),
  deleteEncounter: (id) =>
    fetchWithAuth(`/womens-health/encounters/${id}`, {
      method: 'DELETE',
    }),
  saveConsent: (encounterId, data) =>
    fetchWithAuth(`/womens-health/encounters/${encounterId}/consent`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  saveSymptomIntake: (encounterId, data) =>
    fetchWithAuth(`/womens-health/encounters/${encounterId}/symptom-intake`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  runTriage: (encounterId) =>
    fetchWithAuth(`/womens-health/encounters/${encounterId}/triage`, {
      method: 'POST',
    }),
  generateGuidance: (encounterId) =>
    fetchWithAuth(`/womens-health/encounters/${encounterId}/guidance`, {
      method: 'POST',
    }),
  saveFollowUpPlan: (encounterId, data) =>
    fetchWithAuth(`/womens-health/encounters/${encounterId}/follow-up`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getFollowUpPlan: (encounterId) =>
    fetchWithAuth(`/womens-health/encounters/${encounterId}/follow-up`),
};

// Usage API
export const usageApi = {
  getClinicUsage: () => fetchWithAuth('/usage/clinic'),
};

// Super Admin Stats
export const adminStatsApi = {
  getOverview: () => fetchWithAuth('/admin/stats/overview'),
  getMonthly: () => fetchWithAuth('/admin/stats/monthly'),
  getDoctorsRanking: () => fetchWithAuth('/admin/stats/doctors'),
  getOperational: () => fetchWithAuth('/admin/stats/operational'),
};

// Super Admin Doctor detail
export const adminDoctorsApi = {
  getDoctor: (id) => fetchWithAuth(`/admin/doctors/${id}`),
  getStats: (id) => fetchWithAuth(`/admin/doctors/${id}/stats`),
  getPatients: (id, params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    ).toString();
    return fetchWithAuth(qs ? `/admin/doctors/${id}/patients?${qs}` : `/admin/doctors/${id}/patients`);
  },
};

// Super Admin B2C mother management
export const adminMothersApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    ).toString();
    return fetchWithAuth(qs ? `/admin/mothers?${qs}` : '/admin/mothers');
  },
  get: (id) => fetchWithAuth(`/admin/mothers/${id}`),
};

// FCS API
export const fcsApi = {
  generateGuide: (data) =>
    fetchWithAuth('/fcs/generate-guide', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Helper function to get full file URL
export const getFileUrl = (path) => {
  if (!path) return '';
  // If it's already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // If it's a relative path starting with /api, prepend backend URL
  if (path.startsWith('/api')) {
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  }
  // Otherwise, assume it's a relative API path
  return `${process.env.REACT_APP_BACKEND_URL}/api${path.startsWith('/') ? '' : '/'}${path}`;
};

export default fetchWithAuth;
